/**
 * 管理后台「充值运营」文案（韩、中、英）：充值活动、活动统计、充值转化、赠礼与福利、赠送兑付。
 *
 * 背景：页面按 Alice 的版式复刻，文案保持 Alice 的措辞，品牌与口径按本仓适配：
 *   Alice 的糖豆在这里是花瓣（1 美元 = 1,000 花瓣），人民币换成美元，充值是 Stripe 买固定商品。
 * 设计意图：按页面分组；后端新增的错误码在这里映射（common.ts 的错误表归共用文件，不在本页改），
 *   没映射到的回落到共用表。校验类错误（invalid_campaign、invalid_rule）的原因只有服务端知道，
 *   直接展示服务端给运营看的说明，不在这里逐条翻译。
 * 关键约束：韩文用 해요체；中文不用破折号和英文引号，引号用「」。
 */
import { errorText as baseErrorText } from './common.ts'
import { translate, type Entry, type Locale } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

const CODES: Record<string, Entry> = {
  campaign_conflict: e('活動配置剛被其他管理員改過，已重新載入，請確認後再發布', '다른 관리자가 이벤트 설정을 바꿨어요. 다시 불러왔으니 확인한 뒤 게시해 주세요', 'The campaign was changed by another admin. It has been reloaded, please review and publish again'),
  rule_conflict: e('規則剛被其他管理員改過或已不存在，已重新載入', '다른 관리자가 규칙을 바꾸었거나 규칙이 없어요. 다시 불러왔어요', 'The rule was changed by another admin or no longer exists. It has been reloaded'),
  invalid_amount: e('金額超出上限', '금액이 한도를 넘었어요', 'Amount is over the limit'),
  invalid_days: e('統計天數需在 1 到 365 之間', '통계 일수는 1~365일이어야 해요', 'Days must be between 1 and 365'),
  invalid_active_days: e('活躍窗口需在 1 到 365 天之間', '활성 기간은 1~365일이어야 해요', 'Active window must be between 1 and 365 days'),
  invalid_campaign_key: e('請先選擇活動批次', '이벤트 배치를 먼저 선택해 주세요', 'Select a campaign batch first'),
  not_found: e('記錄不存在或狀態已變化', '기록이 없거나 상태가 바뀌었어요', 'Record not found or its state changed'),
}

/** 服务端校验说明直接展示的错误码。 */
const SERVER_MESSAGE = new Set(['invalid_campaign', 'invalid_rule'])

/** 本组页面的错误提示：先查本组新增的错误码，其余沿用共用错误表。 */
export function errorTextC(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  if (SERVER_MESSAGE.has(code) && err instanceof Error && err.message) return err.message
  const entry = CODES[code]
  return entry ? translate(entry, locale) : baseErrorText(err, locale)
}

export const AG_campaign = {
  common: {
    dash: e('—', '—', '—'),
    loading: e('載入中…', '불러오는 중이에요…', 'Loading…'),
    refresh: e('刷新', '새로고침', 'Refresh'),
    save: e('保存', '저장', 'Save'),
    cancel: e('取消', '취소', 'Cancel'),
    petals: e('花瓣', '꽃잎', 'petals'),
    users: e('使用者', '사용자', 'User'),
    none: e('無', '없음', 'None'),
  },

  // ---------------------------------------------------------------- 充值活动
  campaign: {
    eyebrow: e('RECHARGE CAMPAIGN', 'RECHARGE CAMPAIGN', 'RECHARGE CAMPAIGN'),
    title: e('儲值贈送活動', '충전 보너스 이벤트', 'Recharge bonus campaign'),
    desc: e('統一管理儲值頁文案、檔位和到帳贈送規則', '충전 페이지 문구, 구간, 보너스 지급 규칙을 한곳에서 관리해요', 'Manage recharge page copy, tiers and bonus rules in one place'),
    chipLoading: e('讀取配置中', '설정을 불러오는 중이에요', 'Loading configuration'),
    chipFailed: e('配置讀取失敗', '설정을 불러오지 못했어요', 'Failed to load configuration'),
    note: e(
      '比例加贈按每筆訂單計算；固定活動獎勵可設定每使用者累計上限，按支付成功順序結算。退款不會恢復已領取的活動額度。',
      '비율 보너스는 주문마다 계산해요. 고정 이벤트 보상은 사용자별 누적 한도를 정할 수 있고 결제 성공 순서대로 정산해요. 환불해도 이미 받은 이벤트 한도는 복구되지 않아요.',
      'Rate bonuses are computed per order. Fixed campaign rewards can have a per-user cap and are settled in payment order. Refunds do not restore a claimed campaign allowance.',
    ),
    modeTitle: e('當前活動類型', '현재 이벤트 유형', 'Current campaign type'),
    modeCopy: e('沒有活動時只按商品自帶的贈送到帳；常規和特殊活動都可配置贈送檔位。', '이벤트가 없으면 상품 기본 보너스만 지급해요. 상시·특별 이벤트 모두 보너스 구간을 설정할 수 있어요.', 'With no campaign only the product bonus applies. Regular and special campaigns can both define bonus tiers.'),
    modes: {
      none: { name: e('沒有活動', '이벤트 없음', 'No campaign'), desc: e('隱藏活動說明，不發放額外花瓣', '이벤트 안내를 숨기고 추가 꽃잎을 지급하지 않아요', 'Hide the campaign banner and give no extra petals') },
      regular: { name: e('常規活動', '상시 이벤트', 'Regular campaign'), desc: e('長期儲值檔位，使用穩重暖色視覺', '장기 충전 구간이며 차분한 따뜻한 톤으로 보여줘요', 'Long-running tiers with a calm warm look') },
      special: { name: e('特殊活動', '특별 이벤트', 'Special campaign'), desc: e('節日或限時活動，使用高辨識視覺', '명절·기간 한정 이벤트이며 눈에 띄는 톤으로 보여줘요', 'Holiday or limited-time campaign with a high-contrast look') },
    },
    copyTitle: e('活動說明', '이벤트 안내', 'Campaign copy'),
    copyCopy: e('這裡的內容會直接展示在使用者儲值頁，請明確說明參與條件。', '이 내용은 사용자 충전 페이지에 그대로 보여요. 참여 조건을 분명하게 적어 주세요.', 'This text is shown on the user recharge page. State the conditions clearly.'),
    f: {
      name: e('活動名稱（僅後台）', '이벤트 이름 (관리자용)', 'Campaign name (admin only)'),
      namePh: e('例如：花瓣百日慶典', '예: 꽃잎 100일 기념', 'e.g. Petal 100-day festival'),
      badge: e('活動標籤', '이벤트 라벨', 'Badge'),
      startsAt: e('開始時間（本地時間；留空立即開始）', '시작 시각 (현지 시간, 비우면 즉시 시작)', 'Start (local time; empty = now)'),
      endsAt: e('結束時間（本地時間；留空長期有效）', '종료 시각 (현지 시간, 비우면 계속 유지)', 'End (local time; empty = no end)'),
      rewardMode: e('固定獎勵頻率', '고정 보상 빈도', 'Fixed reward frequency'),
      perOrder: e('每筆符合條件都送', '조건에 맞는 주문마다 지급', 'Every qualifying order'),
      oncePerUser: e('活動期間每使用者僅送一次', '이벤트 기간 중 사용자당 1회', 'Once per user during the campaign'),
      cappedPerUser: e('活動期間每使用者累計封頂', '이벤트 기간 중 사용자별 누적 한도', 'Cumulative cap per user'),
      cap: e('固定獎勵累計上限（花瓣，不含比例加贈）', '고정 보상 누적 한도 (꽃잎, 비율 보너스 제외)', 'Fixed reward cap (petals, excludes rate bonus)'),
      stackMode: e('與常規優惠的關係', '상시 혜택과의 관계', 'Relation to regular offers'),
      stack: e('疊加常規優惠', '상시 혜택과 중복 적용', 'Stack with regular offers'),
      replace: e('替代常規優惠', '상시 혜택을 대체', 'Replace regular offers'),
      key: e('活動批次（同批次共享使用者領取記錄）', '이벤트 배치 (같은 배치는 수령 기록을 공유해요)', 'Campaign batch (shared claim records)'),
      keyPh: e('發布時自動生成', '게시할 때 자동으로 만들어져요', 'Generated on publish'),
      newBatch: e('新批次', '새 배치', 'New batch'),
      title: e('活動標題', '이벤트 제목', 'Campaign title'),
      description: e('活動說明', '이벤트 설명', 'Description'),
      skus: e('適用商品（不選 = 全部在售商品）', '적용 상품 (선택하지 않으면 판매 중인 모든 상품)', 'Applicable products (none = all on sale)'),
      skusEmpty: e('暫無在售商品', '판매 중인 상품이 없어요', 'No products on sale'),
    },
    newBatchConfirmTitle: e('開啟新批次', '새 배치 시작', 'Start a new batch'),
    newBatchConfirm: e('開啟新批次後，所有使用者的活動累計贈送額度會重新計算。確認繼續？', '새 배치를 시작하면 모든 사용자의 이벤트 누적 지급 한도가 처음부터 다시 계산돼요. 계속할까요?', 'Starting a new batch recalculates every user\'s cumulative campaign allowance. Continue?'),
    newBatchDone: e('已生成新活動批次，發布配置後生效', '새 이벤트 배치를 만들었어요. 설정을 게시하면 적용돼요', 'New batch generated. It takes effect after publishing'),
    tiersTitle: e('贈送檔位', '보너스 구간', 'Bonus tiers'),
    tiersCopy: e('同一筆儲值滿足多檔時只取最高檔。「比例 + 固定花瓣」可在比例加贈之上再疊加固定獎勵。', '한 번의 충전이 여러 구간을 만족하면 가장 높은 구간만 적용해요. 「비율 + 고정 꽃잎」은 비율 보너스 위에 고정 보상을 더해요.', 'When several tiers match one top-up only the highest applies. "Rate + fixed" adds a fixed reward on top of the rate bonus.'),
    addTier: e('+ 添加檔位', '+ 구간 추가', '+ Add tier'),
    removeTier: e('刪除檔位', '구간 삭제', 'Remove tier'),
    tier: {
      minUsd: e('儲值達到（美元）', '충전 금액 (USD) 이상', 'Top-up at least (USD)'),
      type: e('贈送方式', '지급 방식', 'Bonus type'),
      fixed: e('固定花瓣', '고정 꽃잎', 'Fixed petals'),
      rate: e('按基礎花瓣比例', '기본 꽃잎 비율', 'Rate of base petals'),
      combined: e('比例 + 固定花瓣', '비율 + 고정 꽃잎', 'Rate + fixed petals'),
      rateField: e('比例加贈（%）', '비율 보너스 (%)', 'Rate bonus (%)'),
      fixedField: e('固定贈送花瓣', '고정 지급 꽃잎', 'Fixed petals'),
      fixedExtra: e('額外固定花瓣', '추가 고정 꽃잎', 'Extra fixed petals'),
      label: e('檔位標籤', '구간 라벨', 'Tier label'),
      labelPh: e('例如：推薦', '예: 추천', 'e.g. Recommended'),
    },
    revision: e('revision {n}', 'revision {n}', 'revision {n}'),
    revisionNone: e('revision —', 'revision —', 'revision —'),
    loaded: e('已載入歷史 r{n} · 發布後生成新版本', '과거 r{n}을 불러왔어요 · 게시하면 새 버전이 만들어져요', 'Loaded history r{n} · publishing creates a new revision'),
    publish: e('發布活動配置', '이벤트 설정 게시', 'Publish campaign'),
    publishing: e('發布中…', '게시하는 중이에요…', 'Publishing…'),
    publishConfirmTitle: e('發布活動配置', '이벤트 설정 게시', 'Publish campaign'),
    publishConfirm: e('確認發布「{mode}」配置？新建立的儲值訂單會立即使用這套規則。', '「{mode}」 설정을 게시할까요? 새로 만드는 충전 주문부터 바로 이 규칙을 써요.', 'Publish the "{mode}" configuration? New recharge orders use it immediately.'),
    published: e('儲值活動已發布', '충전 이벤트를 게시했어요', 'Campaign published'),
    preview: {
      title: e('使用者端預覽', '사용자 화면 미리보기', 'User-side preview'),
      live: e('實時', '실시간', 'Live'),
      baseBadge: e('基礎儲值', '기본 충전', 'Base top-up'),
      baseTitle: e('當前暫無儲值贈送活動', '진행 중인 충전 보너스 이벤트가 없어요', 'No recharge bonus campaign right now'),
      baseDesc: e('儲值商品只按自帶的贈送花瓣到帳。', '충전 상품은 기본 보너스 꽃잎만 지급해요.', 'Products only credit their built-in bonus petals.'),
      specialBadge: e('特別活動', '특별 이벤트', 'Special'),
      regularBadge: e('常規活動', '상시 이벤트', 'Regular'),
      fillTitle: e('填寫活動標題', '이벤트 제목을 입력해 주세요', 'Enter a campaign title'),
      fillDesc: e('填寫清楚的活動參與條件和贈送說明。', '참여 조건과 지급 안내를 분명하게 입력해 주세요.', 'Describe the conditions and rewards clearly.'),
      regularTitle: e('常規儲值優惠', '상시 충전 혜택', 'Regular recharge offer'),
      regularTag: e('長期有效', '상시 진행', 'Always on'),
      specialTitle: e('本次活動優惠', '이번 이벤트 혜택', 'This campaign'),
      specialStackTitle: e('本次活動加贈', '이번 이벤트 추가 보너스', 'Campaign bonus'),
      specialTag: e('限時活動', '기간 한정', 'Limited time'),
      tierRate: e('滿 ${min}{label} · 比例加贈 {rate}%（按商品基礎花瓣）', '${min} 이상{label} · 기본 꽃잎의 {rate}% 추가', 'From ${min}{label} · +{rate}% of base petals'),
      tierFixedRegular: e('滿 ${min}{label} · 常規額外送 {n} 花瓣', '${min} 이상{label} · 상시 혜택 {n} 꽃잎 추가', 'From ${min}{label} · regular extra {n} petals'),
      tierFixedSpecial: e('滿 ${min}{label} · 本次活動額外送 {n} 花瓣', '${min} 이상{label} · 이번 이벤트 {n} 꽃잎 추가', 'From ${min}{label} · campaign extra {n} petals'),
      tierCombined: e('滿 ${min}{label} · 比例加贈 {rate}%，再額外送 {n} 花瓣', '${min} 이상{label} · 비율 {rate}% 추가에 {n} 꽃잎 더 지급', 'From ${min}{label} · +{rate}% plus {n} extra petals'),
      onceNote: e('每位使用者活動期間僅領取一次本次活動的固定獎勵；常規優惠不受影響。', '사용자당 이벤트 기간 중 고정 보상은 한 번만 받아요. 상시 혜택은 영향이 없어요.', 'Each user gets the fixed reward once during the campaign. Regular offers are unaffected.'),
      cappedNote: e('每位使用者本次活動的固定獎勵最多 {cap} 花瓣；常規優惠另計。', '사용자당 이번 이벤트의 고정 보상은 최대 {cap} 꽃잎이에요. 상시 혜택은 별도예요.', 'Each user can receive at most {cap} petals of fixed reward; regular offers are separate.'),
    },
    status: {
      active: e('進行中', '진행 중', 'Active'),
      scheduled: e('待開始', '시작 전', 'Scheduled'),
      ended: e('已結束', '종료', 'Ended'),
      disabled: e('已停用', '중지됨', 'Disabled'),
      superseded: e('已被替代', '대체됨', 'Superseded'),
    },
    modeShort: {
      none: e('無活動', '이벤트 없음', 'None'),
      regular: e('常規', '상시', 'Regular'),
      special: e('特殊', '특별', 'Special'),
    },
    chip: e('{mode} · {status}', '{mode} · {status}', '{mode} · {status}'),
    history: {
      title: e('活動發布歷史', '이벤트 게시 이력', 'Publish history'),
      copy: e('歷史版本不可覆蓋；載入後再次發布會生成新 revision。', '과거 버전은 덮어쓸 수 없어요. 불러온 뒤 다시 게시하면 새 revision이 만들어져요.', 'Past versions are immutable; loading one and publishing again creates a new revision.'),
      effective: e('當前生效：{name}（r{n}）', '현재 적용 중: {name} (r{n})', 'In effect: {name} (r{n})'),
      noEffective: e('無活動', '이벤트 없음', 'no campaign'),
      cols: {
        revision: e('版本', '버전', 'Version'),
        campaign: e('活動', '이벤트', 'Campaign'),
        mode: e('類型', '유형', 'Type'),
        period: e('活動期間', '이벤트 기간', 'Period'),
        reward: e('贈送頻率', '지급 빈도', 'Frequency'),
        stack: e('優惠關係', '혜택 관계', 'Offer relation'),
        status: e('狀態', '상태', 'Status'),
        publishedAt: e('發布時間', '게시 시각', 'Published'),
        actions: e('操作', '작업', 'Actions'),
      },
      unnamed: e('未命名活動', '이름 없는 이벤트', 'Unnamed campaign'),
      immediate: e('立即', '즉시', 'Now'),
      ongoing: e('長期', '계속', 'Ongoing'),
      perOrder: e('每筆', '주문마다', 'Per order'),
      oncePerUser: e('每使用者一次', '사용자당 1회', 'Once per user'),
      cappedPerUser: e('每使用者封頂', '사용자별 한도', 'Capped per user'),
      stack: e('疊加常規', '상시와 중복', 'Stacks'),
      replace: e('替代常規', '상시를 대체', 'Replaces'),
      stats: e('統計', '통계', 'Stats'),
      load: e('載入', '불러오기', 'Load'),
      empty: e('暫無活動歷史', '이벤트 이력이 없어요', 'No campaign history'),
    },
  },

  // ---------------------------------------------------------------- 活动统计
  stats: {
    eyebrow: e('CAMPAIGN ANALYTICS', 'CAMPAIGN ANALYTICS', 'CAMPAIGN ANALYTICS'),
    title: e('活動統計', '이벤트 통계', 'Campaign stats'),
    desc: e('按活動批次查看實收、贈送花瓣與營銷成本', '이벤트 배치별로 실수령액, 지급 꽃잎, 마케팅 비용을 봐요', 'See revenue, bonus petals and marketing cost by campaign batch'),
    chip: e('退款訂單自動剔除', '환불 주문은 자동으로 제외돼요', 'Refunded orders are excluded'),
    sectionTitle: e('活動效果與贈送成本', '이벤트 효과와 지급 비용', 'Campaign effect and bonus cost'),
    sectionTitleFor: e('{name} · 效果與贈送成本', '{name} · 효과와 지급 비용', '{name} · effect and bonus cost'),
    subtitle: e('按活動批次統計支付成功訂單', '이벤트 배치별 결제 성공 주문을 집계해요', 'Paid orders grouped by campaign batch'),
    subtitleKey: e('活動批次：{key} · 1 美元 = 1,000 花瓣', '이벤트 배치: {key} · 1달러 = 1,000 꽃잎', 'Batch: {key} · 1 USD = 1,000 petals'),
    subtitleLoading: e('活動批次：{key} · 統計載入中…', '이벤트 배치: {key} · 통계를 불러오는 중이에요…', 'Batch: {key} · loading stats…'),
    subtitleFailed: e('統計載入失敗：{msg}', '통계를 불러오지 못했어요: {msg}', 'Failed to load stats: {msg}'),
    refresh: e('刷新統計', '통계 새로고침', 'Refresh stats'),
    pick: e('活動批次', '이벤트 배치', 'Campaign batch'),
    noCampaign: e('還沒有發布過活動批次，請先到「儲值活動」發布。', '게시한 이벤트 배치가 아직 없어요. 「충전 이벤트」에서 먼저 게시해 주세요.', 'No campaign batch has been published yet. Publish one under "Recharge campaign" first.'),
    netPaid: e('活動淨實收', '이벤트 순수령액', 'Net revenue'),
    grossNote: e('總收款 {gross} · 退款 {refund}', '총 결제 {gross} · 환불 {refund}', 'Gross {gross} · refunded {refund}'),
    refundedNote: e('退款已剔除', '환불 제외', 'Refunds excluded'),
    users: e('付費使用者', '결제 사용자', 'Paying users'),
    ordersNote: e('{n} 筆有效訂單 · {m} 筆退款', '유효 주문 {n}건 · 환불 {m}건', '{n} valid orders · {m} refunded'),
    regular: e('常規優惠贈送', '상시 혜택 지급', 'Regular bonus'),
    regularNote: e('商品自帶贈送產生的花瓣', '상품 기본 보너스로 지급된 꽃잎', 'Petals from the product built-in bonus'),
    campaign: e('本次活動加贈', '이번 이벤트 추가 지급', 'Campaign bonus'),
    campaignRate: e('佔基礎花瓣 {pct}%', '기본 꽃잎의 {pct}%', '{pct}% of base petals'),
    value: e('活動贈送面值', '이벤트 지급 액면가', 'Bonus face value'),
    valueNote: e('按 1 美元 = 1,000 花瓣折算', '1달러 = 1,000 꽃잎으로 환산', 'At 1 USD = 1,000 petals'),
    total: e('使用者總到帳', '사용자 총 입금', 'Total credited'),
    totalNote: e('基礎花瓣 + 全部贈送', '기본 꽃잎 + 모든 보너스', 'Base petals + all bonuses'),
    costNote: e(
      '活動贈送面值用於核算營銷預算，不等於模型呼叫的實際上遊成本；退款中和已退款訂單不計入有效資料。',
      '이벤트 지급 액면가는 마케팅 예산 계산용이며 모델 호출의 실제 업스트림 비용과 달라요. 환불 중이거나 환불된 주문은 유효 데이터에서 빠져요.',
      'Face value is for marketing budget only and differs from the actual upstream model cost. Refunding and refunded orders are excluded.',
    ),
    costNoteFor: e(
      '本次活動有效加贈 {n} 花瓣，按 1 美元 = 1,000 花瓣折合 ${v} 面值。活動贈送面值用於核算營銷預算，不等於模型呼叫的實際上遊成本；退款訂單對應的 {r} 活動花瓣已從有效口徑剔除。',
      '이번 이벤트의 유효 추가 지급은 {n} 꽃잎이며 1달러 = 1,000 꽃잎 기준 ${v}예요. 액면가는 마케팅 예산 계산용이며 모델 호출의 실제 업스트림 비용과 달라요. 환불 주문의 이벤트 꽃잎 {r}개는 유효 집계에서 뺐어요.',
      'Valid campaign bonus is {n} petals, worth ${v} at 1 USD = 1,000 petals. Face value is for marketing budget only and differs from upstream cost. {r} campaign petals from refunded orders are excluded.',
    ),
    daily: e('每日效果', '일별 효과', 'Daily effect'),
    dailyCols: {
      date: e('日期', '날짜', 'Date'),
      paid: e('實收', '실수령', 'Revenue'),
      usersOrders: e('使用者/訂單', '사용자/주문', 'Users/orders'),
      regular: e('常規贈送', '상시 지급', 'Regular'),
      campaign: e('活動加贈', '이벤트 추가', 'Campaign'),
    },
    dailyEmpty: e('暫無有效支付', '유효 결제가 없어요', 'No valid payments'),
    orders: e('訂單明細（最近 100 筆）', '주문 내역 (최근 100건)', 'Orders (latest 100)'),
    orderCols: {
      time: e('時間', '시각', 'Time'),
      user: e('使用者', '사용자', 'User'),
      amount: e('儲值', '충전', 'Top-up'),
      regular: e('常規贈送', '상시 지급', 'Regular'),
      campaign: e('活動加贈', '이벤트 추가', 'Campaign'),
      total: e('總到帳', '총 입금', 'Total'),
      status: e('狀態', '상태', 'Status'),
    },
    refund: {
      none: e('有效', '유효', 'Valid'),
      partial: e('部分退款', '부분 환불', 'Partial refund'),
      refunded: e('已退款', '환불됨', 'Refunded'),
    },
    ordersEmpty: e('暫無活動訂單', '이벤트 주문이 없어요', 'No campaign orders'),
  },

  // ---------------------------------------------------------------- 充值转化
  funnel: {
    eyebrow: e('RECHARGE JOURNEY ANALYTICS', 'RECHARGE JOURNEY ANALYTICS', 'RECHARGE JOURNEY ANALYTICS'),
    title: e('儲值轉化分析', '충전 전환 분석', 'Recharge funnel'),
    desc: e('通用儲值意向、頁面行為和真實支付不受活動開關影響；活動加贈另作活動維度', '일반 충전 의향, 페이지 행동, 실제 결제는 이벤트 설정과 상관없이 집계해요. 이벤트 보너스는 별도 지표예요', 'General recharge intent, page behavior and real payments are tracked regardless of campaigns; campaign bonus is a separate dimension'),
    days: (n: number): Entry => e(`近 ${n} 天`, `최근 ${n}일`, `Last ${n} days`),
    eligible: e('當前生效活動', '진행 중인 이벤트', 'Active campaigns'),
    eligibleNote: e('{name} · 累計已領 {n}', '{name} · 누적 수령 {n}', '{name} · {n} claimed in total'),
    noPrimary: e('無活動', '이벤트 없음', 'No campaign'),
    exposed: e('儲值入口曝光使用者', '충전 진입점 노출 사용자', 'Users who saw an entry'),
    clickRate: e('入口點選率 {p}%', '진입점 클릭률 {p}%', 'Entry click rate {p}%'),
    checkout: e('建立支付使用者', '결제 생성 사용자', 'Users who started checkout'),
    payRate: e('支付轉化 {p}% · {n} 筆訂單', '결제 전환 {p}% · 주문 {n}건', 'Pay conversion {p}% · {n} orders'),
    claimed: e('活動領取使用者', '이벤트 수령 사용자', 'Users who claimed a bonus'),
    claimRate: e('曝光領取率 {p}%', '노출 대비 수령률 {p}%', 'Exposure to claim {p}%'),
    revenue: e('儲值淨實收', '충전 순수령액', 'Net revenue'),
    aov: e('客單價 ${a} · 退款 ${r}', '객단가 ${a} · 환불 ${r}', 'AOV ${a} · refunded ${r}'),
    bonusValue: e('活動加贈面值', '이벤트 보너스 액면가', 'Campaign bonus value'),
    bonus: e('{n} 花瓣 · 退款關聯 {m}', '{n} 꽃잎 · 환불 연관 {m}', '{n} petals · {m} tied to refunds'),
    noteLoading: e('正在核對行為事件、支付帳本和訂單履約…', '행동 이벤트, 결제 원장, 주문 이행을 대조하는 중이에요…', 'Reconciling events, payment ledger and order fulfillment…'),
    note: e(
      '儲值漏鬥不受活動開關控制；訂單與支付統計所選期間內全部真實 Stripe 儲值，活動加贈與留存按訂單上的活動結算記錄歸因。加贈面值按 1 美元 = 1,000 花瓣折算，不等於實際上遊成本。統計時區：UTC。',
      '충전 퍼널은 이벤트 설정과 무관해요. 주문과 결제는 선택 기간의 실제 Stripe 충전 전체를 집계하고, 이벤트 보너스와 리텐션은 주문의 이벤트 정산 기록 기준이에요. 액면가는 1달러 = 1,000 꽃잎 기준이며 실제 업스트림 비용과 달라요. 시간대: UTC.',
      'The funnel ignores campaign switches. Orders and payments cover all real Stripe top-ups in the range; bonus and retention follow the campaign settlement on each order. Face value uses 1 USD = 1,000 petals and is not the upstream cost. Timezone: UTC.',
    ),
    noteFailed: e('統計載入失敗：{msg}', '통계를 불러오지 못했어요: {msg}', 'Failed to load stats: {msg}'),
    funnelTitle: e('通用儲值轉化漏鬥', '일반 충전 전환 퍼널', 'Recharge conversion funnel'),
    funnelSub: e('活動關閉仍採集；人數按帳號去重，訂單與支付讀取服務端權威帳本', '이벤트를 꺼도 수집해요. 사람 수는 계정 기준으로 중복 제거하고 주문과 결제는 서버 원장을 읽어요', 'Collected even with no campaign; counted per account, orders and payments come from the server ledger'),
    funnelEmpty: e('暫無漏鬥資料', '퍼널 데이터가 없어요', 'No funnel data'),
    base: e('基準', '기준', 'Base'),
    steps: {
      entry_clicked: e('點選儲值入口', '충전 진입점 클릭', 'Entry clicked'),
      page_viewed: e('到達儲值頁', '충전 페이지 도착', 'Page viewed'),
      tier_selected: e('選擇檔位', '구간 선택', 'Tier selected'),
      recharge_clicked: e('點選儲值', '충전 버튼 클릭', 'Recharge clicked'),
      checkout_started: e('建立支付', '결제 생성', 'Checkout started'),
      paid: e('支付成功', '결제 성공', 'Paid'),
    } as Record<string, Entry>,
    qualityTitle: e('領取後品質', '수령 후 품질', 'Post-claim quality'),
    qualitySub: e('使用為領取後發生成功計費；復購為後續有效 Stripe 支付', '사용은 수령 후 성공 과금이 생긴 경우이고, 재구매는 이후 유효한 Stripe 결제예요', 'Usage means a billed success after claiming; repeat means a later valid Stripe payment'),
    mini: {
      orderFailed: e('下單失敗', '주문 생성 실패', 'Order failures'),
      orderFailedNote: e('未建立訂單的支付意向', '주문이 만들어지지 않은 결제 의향', 'Intents that never became an order'),
      cohort: e('領取隊列', '수령 코호트', 'Claim cohort'),
      cohortNote: e('所選期間成功領取', '선택 기간에 수령한 사용자', 'Claimed within the range'),
      usage1: e('1 天內使用', '1일 내 사용', 'Used within 1 day'),
      usage7: e('7 天內使用', '7일 내 사용', 'Used within 7 days'),
      usage30: e('30 天內使用', '30일 내 사용', 'Used within 30 days'),
      repeat7: e('7 天復購', '7일 내 재구매', 'Repeat within 7 days'),
      repeat30: e('30 天復購', '30일 내 재구매', 'Repeat within 30 days'),
      people: e('{n} 人', '{n}명', '{n} users'),
      pageCheckout: e('儲值頁下單率', '충전 페이지 주문 전환율', 'Page to checkout'),
      pageViewed: e('{n} 人到達', '{n}명 도착', '{n} reached the page'),
      pending: e('待支付訂單', '결제 대기 주문', 'Pending orders'),
      pendingNote: e('用於排查支付流失', '결제 이탈 점검용이에요', 'For investigating drop-off'),
    },
    placementTitle: e('入口效果', '진입점 효과', 'Entry performance'),
    placementSub: e('控制台各入口曝光、點選、到達', '콘솔 진입점별 노출, 클릭, 도착', 'Exposure, clicks and arrivals per console entry'),
    placementCols: {
      name: e('入口', '진입점', 'Entry'),
      exposed: e('曝光', '노출', 'Exposed'),
      clicked: e('點選', '클릭', 'Clicked'),
      viewed: e('到達', '도착', 'Arrived'),
      rate: e('點選率', '클릭률', 'Click rate'),
    },
    placementEmpty: e('暫無入口事件', '진입점 이벤트가 없어요', 'No entry events'),
    sourceTitle: e('下單商品', '주문 상품', 'Ordered products'),
    sourceSub: e('按訂單上的商品歸因，不信任前端自報', '주문에 기록된 상품 기준이며 프런트엔드 자가 보고는 믿지 않아요', 'Attributed from the order itself, not client-reported'),
    sourceCols: {
      name: e('商品', '상품', 'Product'),
      users: e('使用者', '사용자', 'Users'),
      orders: e('訂單', '주문', 'Orders'),
      paid: e('支付', '결제', 'Paid'),
      rate: e('轉化', '전환', 'Conversion'),
      revenue: e('實收', '실수령', 'Revenue'),
    },
    sourceEmpty: e('暫無儲值訂單', '충전 주문이 없어요', 'No recharge orders'),
    segmentTitle: e('人群與檔位', '사용자군과 구간', 'Audience and batches'),
    segmentSub: e('首充與復購人群，以及各活動批次的獎勵貢獻', '첫 충전·재구매 사용자군과 이벤트 배치별 보상 기여', 'First vs repeat payers and the reward share of each campaign batch'),
    segmentCols: {
      name: e('分組', '그룹', 'Group'),
      users: e('使用者', '사용자', 'Users'),
      revenue: e('實收', '실수령', 'Revenue'),
      bonus: e('加贈', '추가 보너스', 'Bonus'),
    },
    segmentAudience: e('人群 · {name}', '사용자군 · {name}', 'Audience · {name}'),
    segmentBatch: e('批次 · {name}', '배치 · {name}', 'Batch · {name}'),
    noBatch: e('無活動', '이벤트 없음', 'No campaign'),
    segmentEmpty: e('暫無領取資料', '수령 데이터가 없어요', 'No claim data'),
    labels: {
      first: e('首次儲值', '첫 충전', 'First top-up'),
      repeat: e('復購', '재구매', 'Repeat'),
      unknown: e('未知入口', '알 수 없는 진입점', 'Unknown entry'),
    } as Record<string, Entry>,
    fulfillTitle: e('訂單履約、資料健康與風險', '주문 이행, 데이터 상태, 위험', 'Fulfillment, data health and risk'),
    fulfillSub: e('花瓣帳本一致性、到帳履約、缺失歸因及異常支付信號', '꽃잎 원장 일치, 입금 이행, 누락된 귀속, 이상 결제 신호', 'Petal ledger consistency, crediting, missing attribution and unusual payments'),
    fulfill: {
      rate: e('履約成功率', '이행 성공률', 'Fulfillment rate'),
      rateNote: e('已到帳 / 全部已付訂單', '입금 완료 / 결제된 전체 주문', 'Credited / all paid orders'),
      fulfilled: e('已到帳', '입금 완료', 'Credited'),
      fulfilledNote: e('花瓣已入錢包', '꽃잎이 지갑에 들어갔어요', 'Petals are in the wallet'),
      pending: e('待到帳', '입금 대기', 'Awaiting credit'),
      pendingNote: e('等待自動履約', '자동 이행을 기다리는 중이에요', 'Waiting for automatic fulfillment'),
      retrying: e('重試中', '재시도 중', 'Retrying'),
      retryingNote: e('履約失敗後自動重試', '이행 실패 후 자동으로 재시도해요', 'Retrying after a failure'),
      refunded: e('已退款', '환불됨', 'Refunded'),
      refundedNote: e('整單退款或撤銷', '전액 환불 또는 취소', 'Fully refunded or revoked'),
      mismatch: e('金額不符', '금액 불일치', 'Amount mismatch'),
      mismatchNote: e('需要排障或補發', '점검이나 재지급이 필요해요', 'Needs investigation or reissue'),
    },
    qualityHealthy: e(
      '資料健康。風險觀察：重複建立訂單 {multi} 人；超 24 小時待支付 {stale} 筆；關閉後又支付 {late} 筆；領取後退款 {refunded} 筆。',
      '데이터가 정상이에요. 위험 관찰: 주문 중복 생성 {multi}명, 24시간 넘은 결제 대기 {stale}건, 닫힌 뒤 결제 {late}건, 수령 후 환불 {refunded}건.',
      'Data looks healthy. Risk watch: repeat order creators {multi}; pending over 24h {stale}; paid after close {late}; refunded after claim {refunded}.',
    ),
    qualityWarn: e(
      '需要關注：缺少活動歸因 {missing} 筆；金額不一致 {mismatch} 筆；超 15 分鐘仍未到帳 {stuck} 筆。風險觀察：重複建立訂單 {multi} 人；超 24 小時待支付 {stale} 筆；關閉後又支付 {late} 筆；領取後退款 {refunded} 筆。',
      '확인이 필요해요: 이벤트 귀속 누락 {missing}건, 금액 불일치 {mismatch}건, 15분 넘게 입금되지 않은 주문 {stuck}건. 위험 관찰: 주문 중복 생성 {multi}명, 24시간 넘은 결제 대기 {stale}건, 닫힌 뒤 결제 {late}건, 수령 후 환불 {refunded}건.',
      'Needs attention: missing campaign attribution {missing}; amount mismatch {mismatch}; not credited after 15 min {stuck}. Risk watch: repeat order creators {multi}; pending over 24h {stale}; paid after close {late}; refunded after claim {refunded}.',
    ),
    dailyTitle: e('每日趨勢', '일별 추이', 'Daily trend'),
    dailySub: e('UTC；支付金額已剔除退款中及已退款訂單', 'UTC 기준이며 결제 금액에서 환불 중·환불된 주문은 뺐어요', 'UTC; payment amounts exclude refunding and refunded orders'),
    dailyCols: {
      date: e('日期', '날짜', 'Date'),
      exposed: e('曝光', '노출', 'Exposed'),
      viewed: e('到達', '도착', 'Arrived'),
      orders: e('下單', '주문', 'Orders'),
      paidUsers: e('支付使用者', '결제 사용자', 'Paying users'),
      revenue: e('實收', '실수령', 'Revenue'),
      claimed: e('領取', '수령', 'Claimed'),
      bonus: e('加贈', '추가 보너스', 'Bonus'),
    },
    dailyEmpty: e('暫無每日資料', '일별 데이터가 없어요', 'No daily data'),
    claimsTitle: e('最近領取明細', '최근 수령 내역', 'Recent claims'),
    claimsSub: e('最多 100 筆；履約失敗原因僅供內部排障', '최대 100건이며 이행 실패 사유는 내부 점검용이에요', 'Up to 100 rows; failure reasons are for internal debugging'),
    claimsCols: {
      time: e('領取時間', '수령 시각', 'Claimed at'),
      user: e('使用者', '사용자', 'User'),
      audience: e('人群', '사용자군', 'Audience'),
      source: e('來源', '출처', 'Source'),
      order: e('訂單', '주문', 'Order'),
      amount: e('儲值', '충전', 'Top-up'),
      bonus: e('加贈', '추가 보너스', 'Bonus'),
      fulfillment: e('履約', '이행', 'Fulfillment'),
    },
    fulfillStatus: {
      fulfilled: e('已到帳', '입금 완료', 'Credited'),
      paid: e('待到帳', '입금 대기', 'Awaiting credit'),
      refunded: e('已退款', '환불됨', 'Refunded'),
      revoked: e('已撤銷', '취소됨', 'Revoked'),
    } as Record<string, Entry>,
    claimsEmpty: e('暫無領取記錄', '수령 기록이 없어요', 'No claims yet'),
  },

  // ---------------------------------------------------------------- 赠礼与福利
  reward: {
    eyebrow: e('GIFTS & REWARDS', 'GIFTS & REWARDS', 'GIFTS & REWARDS'),
    title: e('贈禮與福利', '선물과 혜택', 'Rewards & gifts'),
    desc: e('帳號贈禮金額（註冊禮）與模型用量達標獎勵', '계정 선물 금액(가입 선물)과 모델 사용량 달성 보상', 'Account gift amounts (signup gift) and model-usage rewards'),
    chip: e('達標判斷不阻塞推理', '달성 판정은 추론을 막지 않아요', 'Reward checks never block inference'),
    note: e(
      '每條規則獨立生效：帳號在起算時間後、模型 ID 命中匹配子串的成功帳單 Token（輸入 + 輸出）累計達到閾值即觸發；同一規則每個帳號終身只發一次，規則改動不影響已發記錄。獎勵以贈送花瓣到帳，後台每 30 秒掃描一次，新用量約 2 分鐘後計入。',
      '규칙마다 독립적으로 적용돼요. 시작 시각 이후 모델 ID가 일치 문자열에 맞는 성공 과금 토큰(입력 + 출력)이 임계값에 도달하면 발동해요. 같은 규칙은 계정당 평생 한 번만 지급하고, 규칙을 바꿔도 이미 지급한 기록은 그대로예요. 보상은 보너스 꽃잎으로 들어가며 백그라운드에서 30초마다 스캔하고 새 사용량은 약 2분 뒤 반영돼요.',
      'Each rule stands alone: once an account\'s successful billed tokens (input + output) on models matching the substring reach the threshold after the start time, it triggers. A rule pays each account once for life and edits never change past grants. Rewards arrive as bonus petals; a worker scans every 30 seconds and new usage counts after about 2 minutes.',
    ),
    giftsTitle: e('帳號贈禮', '계정 선물', 'Account gifts'),
    giftsCopy: e('註冊禮的金額，保存後下一個註冊的使用者即生效，無需重啟服務；已發出的贈禮不會被追溯改寫。填 0 = 停發這份禮。', '가입 선물 금액은 저장하면 다음 가입자부터 바로 적용돼요. 서비스를 재시작할 필요가 없고 이미 지급한 선물은 바뀌지 않아요. 0을 넣으면 이 선물을 중단해요.', 'The signup gift amount applies to the next signup right after saving, no restart needed; gifts already given stay unchanged. Enter 0 to stop this gift.'),
    giftStat: e('註冊禮已發 {n} 份 · 今日 {m} 份 · 成本 {c} 花瓣', '가입 선물 {n}건 지급 · 오늘 {m}건 · 비용 {c} 꽃잎', 'Signup gifts given {n} · today {m} · cost {c} petals'),
    giftCols: {
      gift: e('贈禮', '선물', 'Gift'),
      desc: e('說明', '설명', 'Description'),
      amount: e('金額', '금액', 'Amount'),
      status: e('狀態', '상태', 'Status'),
    },
    giftNames: {
      signup: {
        label: e('註冊禮', '가입 선물', 'Signup gift'),
        desc: e('電子信箱註冊驗證通過、建立帳號時到帳，每個帳號終身一次', '이메일 가입 인증이 끝나 계정이 만들어질 때 지급해요. 계정당 평생 한 번이에요', 'Credited when email signup is verified and the account is created, once per account'),
      },
    } as Record<string, { label: Entry; desc: Entry }>,
    giftSave: e('保存', '저장', 'Save'),
    giftStopped: e('已停發', '중단됨', 'Stopped'),
    giftCustom: e('已改額 · 預設 {n} 花瓣 · ', '금액 변경됨 · 기본 {n} 꽃잎 · ', 'Changed · default {n} petals · '),
    giftRestore: e('恢復', '복원', 'Restore'),
    giftDefault: e('預設額度', '기본 금액', 'Default amount'),
    giftEmpty: e('暫無贈禮配置項', '선물 설정이 없어요', 'No gifts configured'),
    giftNeedAmount: e('請輸入金額（花瓣）', '금액(꽃잎)을 입력해 주세요', 'Enter an amount (petals)'),
    giftZeroTitle: e('停發贈禮', '선물 중단', 'Stop gift'),
    giftZeroConfirm: e('填 0 會停發這份贈禮，之後註冊的使用者拿不到花瓣。確認？', '0을 넣으면 이 선물을 중단해요. 이후 가입하는 사용자는 꽃잎을 받지 못해요. 계속할까요?', 'Entering 0 stops this gift; later signups will get no petals. Continue?'),
    giftSaved: e('已改為 {n} 花瓣，立即生效', '{n} 꽃잎으로 바꿨어요. 바로 적용돼요', 'Changed to {n} petals, effective immediately'),
    giftStoppedToast: e('已停發這份贈禮', '이 선물을 중단했어요', 'This gift is stopped'),
    giftRestored: e('已恢復預設額度', '기본 금액으로 복원했어요', 'Default amount restored'),
    rulesTitle: e('福利規則', '혜택 규칙', 'Reward rules'),
    rulesCopy: e('停用規則立即停止新增達標判斷；獎勵類型 badge 可保存但暫不可啟用。', '규칙을 중지하면 새 달성 판정이 바로 멈춰요. 보상 유형 badge는 저장만 되고 아직 켤 수 없어요.', 'Disabling a rule stops new checks immediately; the badge reward type can be saved but not yet enabled.'),
    newRule: e('新建規則', '규칙 만들기', 'New rule'),
    ruleCols: {
      id: e('ID', 'ID', 'ID'),
      name: e('名稱', '이름', 'Name'),
      status: e('狀態', '상태', 'Status'),
      model: e('模型匹配', '모델 일치', 'Model match'),
      threshold: e('Token 閾值', '토큰 임계값', 'Token threshold'),
      reward: e('獎勵', '보상', 'Reward'),
      start: e('起算時間', '집계 시작', 'Counting from'),
      granted: e('已發放', '지급', 'Granted'),
      failed: e('失敗', '실패', 'Failed'),
      actions: e('操作', '작업', 'Actions'),
    },
    ruleEnabled: e('啟用中', '사용 중', 'Enabled'),
    ruleDisabled: e('已停用', '중지됨', 'Disabled'),
    allModels: e('全部模型', '모든 모델', 'All models'),
    delivered: e('（送達 {n}）', '(전달 {n})', '({n} delivered)'),
    edit: e('編輯', '편집', 'Edit'),
    rulesEmpty: e('還沒有規則，點選右上角「新建規則」開始', '규칙이 없어요. 오른쪽 위 「규칙 만들기」로 시작해 보세요', 'No rules yet. Click "New rule" at the top right to start'),
    rewardTypes: {
      bonus: e('花瓣', '꽃잎', 'Petals'),
      badge: e('徽章', '배지', 'Badge'),
    } as Record<string, Entry>,
    grantsTitle: e('發放記錄', '지급 기록', 'Grant records'),
    grantsCopy: e('pending 由後台自動重試（15 秒起指數退避）；failed 可在恢復後人工重試。', 'pending은 백그라운드에서 자동 재시도해요(15초부터 지수 백오프). failed는 복구한 뒤 직접 재시도할 수 있어요.', 'Pending grants retry automatically (exponential backoff from 15s); failed ones can be retried manually once fixed.'),
    allRules: e('全部規則', '모든 규칙', 'All rules'),
    allStatus: e('全部狀態', '모든 상태', 'All statuses'),
    grantStatus: {
      pending: e('發放中', '지급 중', 'Pending'),
      delivered: e('已送達', '전달됨', 'Delivered'),
      owned: e('已擁有', '이미 보유', 'Owned'),
      failed: e('失敗', '실패', 'Failed'),
    } as Record<string, Entry>,
    grantCols: {
      time: e('時間', '시각', 'Time'),
      user: e('使用者', '사용자', 'User'),
      rule: e('規則', '규칙', 'Rule'),
      reward: e('獎勵', '보상', 'Reward'),
      status: e('狀態', '상태', 'Status'),
      attempts: e('嘗試', '시도', 'Attempts'),
      error: e('最近錯誤', '최근 오류', 'Last error'),
      actions: e('操作', '작업', 'Actions'),
    },
    retry: e('重試', '재시도', 'Retry'),
    retried: e('已重試，當前狀態：{s}', '재시도했어요. 현재 상태: {s}', 'Retried, status now: {s}'),
    grantsEmpty: e('暫無發放記錄', '지급 기록이 없어요', 'No grant records'),
    modalNew: e('新建用量福利規則', '사용량 혜택 규칙 만들기', 'New usage reward rule'),
    modalEdit: e('編輯規則 #{id}', '규칙 #{id} 편집', 'Edit rule #{id}'),
    f: {
      name: e('規則名稱（僅後台）', '규칙 이름 (관리자용)', 'Rule name (admin only)'),
      namePh: e('例如：GPT 百萬 Token 送 100 花瓣', '예: GPT 100만 토큰 달성 시 100 꽃잎', 'e.g. 1M GPT tokens earns 100 petals'),
      model: e('模型匹配子串（留空匹配全部）', '모델 일치 문자열 (비우면 전체)', 'Model substring (empty = all)'),
      modelPh: e('例如 gpt', '예: gpt', 'e.g. gpt'),
      threshold: e('Token 閾值（輸入 + 輸出）', '토큰 임계값 (입력 + 출력)', 'Token threshold (input + output)'),
      type: e('獎勵類型', '보상 유형', 'Reward type'),
      typeBonus: e('花瓣', '꽃잎', 'Petals'),
      typeBadge: e('徽章（執行器未接入）', '배지 (실행기 미연결)', 'Badge (executor not connected)'),
      refBonus: e('贈送花瓣數（1 到 100000）', '지급 꽃잎 수 (1~100000)', 'Petals to give (1 to 100000)'),
      refBonusPh: e('例如 100', '예: 100', 'e.g. 100'),
      refBadge: e('徽章編碼（執行器未接入，暫不可啟用）', '배지 코드 (실행기 미연결, 아직 켤 수 없어요)', 'Badge code (executor not connected, cannot be enabled yet)'),
      refBadgePh: e('例如 badge_gpt_million', '예: badge_gpt_million', 'e.g. badge_gpt_million'),
      start: e('累計起算時間（本地時間；留空 = 保存時刻起算）', '집계 시작 시각 (현지 시간, 비우면 저장 시점)', 'Count from (local time; empty = save time)'),
      enabled: e('啟用規則（花瓣類型需先填寫贈送數量）', '규칙 사용 (꽃잎 유형은 지급 수를 먼저 입력해 주세요)', 'Enable the rule (petal rewards need an amount first)'),
      save: e('保存規則', '규칙 저장', 'Save rule'),
    },
    created: e('規則已建立', '규칙을 만들었어요', 'Rule created'),
    updated: e('規則已更新', '규칙을 수정했어요', 'Rule updated'),
  },

  // ---------------------------------------------------------------- 赠送兑付
  redeem: {
    eyebrow: e('GRANT LIABILITY', 'GRANT LIABILITY', 'GRANT LIABILITY'),
    title: e('贈送兌付', '선물 지급 현황', 'Gift redemption'),
    desc: e('每批發出去的花瓣被用掉多少，剩下的還有多少可能變成真實成本', '배치별로 지급한 꽃잎이 얼마나 쓰였고, 남은 것 중 얼마가 실제 비용이 될 수 있는지 봐요', 'How much of each batch of petals has been spent, and how much of the rest may still turn into real cost'),
    chip: e('發放成本回看', '지급 비용 회고', 'Grant cost review'),
    granted: e('累計發放', '누적 지급', 'Total granted'),
    grantedSub: e('{n} 名持有者', '보유자 {n}명', '{n} holders'),
    redeemed: e('已兌付', '사용 완료', 'Redeemed'),
    rateSub: e('兌付率 {r}%', '사용률 {r}%', 'Redemption {r}%'),
    outstanding: e('未兌付', '미사용', 'Outstanding'),
    outstandingSub: e('尚未變成消耗的部分', '아직 소모되지 않은 부분', 'Not yet turned into usage'),
    live: e('有效負債', '유효 부채', 'Live liability'),
    liveSub: e('{n} 人近 {d} 天有呼叫', '{n}명이 최근 {d}일 안에 호출했어요', '{n} holders called in the last {d} days'),
    dormant: e('已沉睡（非成本）', '휴면 (비용 아님)', 'Dormant (not cost)'),
    dormantSub: e('{n} 人已沉睡，佔未兌付 {p}%', '{n}명이 휴면 중이며 미사용분의 {p}%예요', '{n} dormant holders, {p}% of outstanding'),
    internal: e('內部帳號發放', '내부 계정 지급', 'Internal grants'),
    internalSub: e('{n} 個內部帳號，不計入對外負債', '내부 계정 {n}개이며 대외 부채에 넣지 않아요', '{n} internal accounts, excluded from liability'),
    petalsUsd: e('{p} (${u})', '{p} (${u})', '{p} (${u})'),
    activeWindow: e('活躍窗口', '활성 기간', 'Active window'),
    activeOption: (d: number): Entry => e(`近 ${d} 天有呼叫`, `최근 ${d}일 호출 있음`, `Called in last ${d} days`),
    activeHint: e('窗口內沒有任何呼叫的持有者，其未兌付花瓣按不會兌付看待', '기간 안에 호출이 없는 보유자의 미사용 꽃잎은 사용되지 않는 것으로 봐요', 'Holders with no calls in the window are assumed never to redeem'),
    channelsTitle: e('按渠道', '채널별', 'By channel'),
    channelsCopy: e('各發放渠道的兌付效率對比', '지급 채널별 사용 효율 비교', 'Redemption efficiency by grant channel'),
    channelCols: {
      channel: e('渠道', '채널', 'Channel'),
      users: e('人數', '인원', 'Holders'),
      granted: e('發放', '지급', 'Granted'),
      redeemed: e('已兌付', '사용 완료', 'Redeemed'),
      rate: e('兌付率', '사용률', 'Rate'),
      outstanding: e('未兌付', '미사용', 'Outstanding'),
      live: e('其中活躍持有', '활성 보유분', 'Held by active'),
      dormant: e('其中沉睡', '휴면분', 'Dormant'),
    },
    channels: {
      order: e('儲值贈送', '충전 보너스', 'Top-up bonus'),
      usage_reward: e('用量福利', '사용량 혜택', 'Usage reward'),
      account_gift: e('帳號贈禮', '계정 선물', 'Account gift'),
      admin: e('管理員發放', '관리자 지급', 'Admin grant'),
      activation: e('啟用碼', '활성화 코드', 'Activation code'),
      reversal: e('衝正返還', '정정 반환', 'Reversal'),
      other: e('其他', '기타', 'Other'),
    } as Record<string, Entry>,
    batchesTitle: e('按批次', '배치별', 'By batch'),
    batchesCopy: e('發放事由即批次；儲值贈送按活動批次歸組，沒有參與活動的訂單歸為「商品自帶贈送」', '지급 사유가 곧 배치예요. 충전 보너스는 이벤트 배치로 묶고 이벤트에 참여하지 않은 주문은 「상품 기본 보너스」로 모아요', 'The grant reason is the batch; top-up bonuses group by campaign batch and orders outside any campaign fall under "Product built-in bonus"'),
    batchCols: {
      batch: e('批次', '배치', 'Batch'),
      channel: e('渠道', '채널', 'Channel'),
      users: e('人數', '인원', 'Holders'),
      live: e('活躍', '활성', 'Active'),
      granted: e('發放', '지급', 'Granted'),
      redeemed: e('已兌付', '사용 완료', 'Redeemed'),
      rate: e('兌付率', '사용률', 'Rate'),
      outstanding: e('未兌付', '미사용', 'Outstanding'),
      dormant: e('其中沉睡', '휴면분', 'Dormant'),
      last: e('最後發放', '마지막 지급', 'Last grant'),
    },
    batchDefault: e('商品自帶贈送', '상품 기본 보너스', 'Product built-in bonus'),
    topTitle: e('未兌付最多的持有者', '미사용이 가장 많은 보유자', 'Largest outstanding holders'),
    topCopy: e('已沉睡的大額帳號是可以回收的敞口，活躍的則要預備真實成本', '휴면 중인 대규모 계정은 회수할 수 있는 노출이고, 활성 계정은 실제 비용으로 준비해야 해요', 'Large dormant accounts are exposure you can reclaim; active ones need real cost reserved'),
    topCols: {
      user: e('使用者', '사용자', 'Holder'),
      granted: e('獲贈', '받은 양', 'Received'),
      redeemed: e('已兌付', '사용 완료', 'Redeemed'),
      rate: e('兌付率', '사용률', 'Rate'),
      outstanding: e('未兌付', '미사용', 'Outstanding'),
      balance: e('當前餘額', '현재 잔액', 'Balance'),
      lastSeen: e('最後活躍', '마지막 활동', 'Last active'),
      status: e('狀態', '상태', 'Status'),
    },
    live_: e('活躍', '활성', 'Active'),
    dormant_: e('沉睡', '휴면', 'Dormant'),
    neverCalled: e('從未呼叫', '호출 기록 없음', 'Never called'),
    empty: e('暫無資料', '데이터가 없어요', 'No data'),
  },
}
